import React, { useEffect, useRef } from 'react'
import { useDrag } from 'react-dnd'
import { useMediaQuery } from 'react-responsive'
import Checkbox from '@mui/material/Checkbox'
import { mediaQueryForMobile } from '../../HomePage/HomePage'
import { DealModal } from '../../PricingListPage/DealModal'
import { useModalsStore } from '../../../stores/modalsStore'
import { usePipelineStore } from '../stores/pipelineStore'

export const OPPORTUNITY_DND_TYPE = 'CRM_OPPORTUNITY'

// No per-user color in the schema, so derive one deterministically from the
// owner id off a fixed palette (matches the mock's colored avatars / borders).
const OWNER_COLORS = ['#7048cc', '#0f9d8f', '#4A90E2', '#2e9967', '#d07020', '#c93535', '#3578c8', '#b45309']
export const ownerColor = (id) => OWNER_COLORS[Math.abs(Number(id) || 0) % OWNER_COLORS.length]

const initialsOf = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)
    return letters.toUpperCase()
}

// The day the lead came in, as M/D. The API sends UTC ISO; read the local
// calendar fields so the date matches the rep's own clock (and every other
// date in the CRM, which all format locally).
const createdLabel = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return `Created ${d.getMonth() + 1}/${d.getDate()}`
}

// `selection` turns the card into a picker (AttachToLeadsModal). Absent — which
// is how the board itself always renders it — every branch below is inert and
// the card behaves exactly as it always has.
// `dropShift` (px) moves the card down to open a drop gap above it.
export const OpportunityCard = ({ opportunity, onOpen, stages, onMove, selection, dropShift = 0 }) => {
    const deepLinked = usePipelineStore((state) => state.focusedOpportunityId === opportunity.id)
    // One owner's board (Main User or a single user): every card is theirs, so
    // the avatar says nothing. Entire Company keeps it.
    const showOwner = usePipelineStore((state) => state.repFilter === 'all')
    const selecting = Boolean(selection)
    // The spotlight is the real board's; a picked card must not inherit it (or
    // scroll itself into view inside the modal because of it).
    const isFocused = deepLinked && !selecting
    const isSelected = selecting && selection.selectedIds.has(opportunity.id)

    const cardRef = useRef(null)
    const [{ isDragging }, dragRef] = useDrag(
        () => ({
            type: OPPORTUNITY_DND_TYPE,
            // Measured as the drag starts: the drop gap is sized to this card.
            item: () => ({ id: opportunity.id, fromStage: opportunity.stage, height: cardRef.current?.offsetHeight || 0 }),
            // A picked card must not be draggable: a drag here would PATCH a
            // real stage change from a modal that is only meant to read.
            canDrag: !selecting,
            collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        }),
        [opportunity.id, opportunity.stage, selecting]
    )

    const person = opportunity.person_detail || {}
    const owner = opportunity.owner_detail || {}
    const color = ownerColor(owner.id)
    // One source for the visible name and the checkbox's label, so the two
    // can't drift apart.
    const personName = person.name || 'Unnamed'

    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const openModal = useModalsStore((s) => s.openModal)

    // Every titled quote, the selected deal first, each id once.
    const quotes = [opportunity.selected_deal_detail, ...(opportunity.quotes_detail || [])]
        .filter((q, i, all) => q?.description && (q.id == null || all.findIndex((o) => o?.id === q.id) === i))
    const vehicle = quotes.map((q) => q.description).join(', ') || 'No vehicle yet'

    // Same as QuoteCard. Stops the card's click too: it navigates to the
    // person, and navigation closes every open modal.
    const openQuote = (e, id) => {
        e.stopPropagation()
        e.preventDefault()
        if (isTabletOrMobile) {
            window.open(`/quotes/${id}`, '_blank')
            return
        }
        openModal({ element: DealModal, id: 'DealModal', props: { id } })
    }

    const handleCardClick = () => {
        if (selecting) {
            selection.onToggle(opportunity)
            return
        }
        if (onOpen) onOpen(opportunity)
    }

    // Scrolls itself into view when a deep link names it. Done here rather
    // than by the linking hook because the card may not exist yet — the column
    // it lives in is paginated, so it can mount several pages after the link
    // was followed.
    useEffect(() => {
        if (!isFocused || !cardRef.current) return
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, [isFocused])

    return (
        <div
            ref={(node) => { cardRef.current = node; dragRef(node) }}
            data-opportunity-id={opportunity.id}
            className={`card${isDragging ? ' dragging' : ''}${isFocused ? ' focused' : ''}`
                + `${selecting ? ' is-selectable' : ''}${isSelected ? ' is-selected' : ''}`}
            style={dropShift ? { borderLeftColor: color, transform: `translateY(${dropShift}px)` } : { borderLeftColor: color }}
            onClick={handleCardClick}
        >
            <div className="card-top">
                <div className="card-info">
                    <span className="card-name">{personName}</span>
                    {person.phone_number && <span className="card-sub">{person.phone_number}</span>}
                </div>
                {/* In this row, not its own, so the card keeps its height. */}
                {opportunity.is_renewal && <span className="renewal-flag">Renewal</span>}
                {/* The checkbox takes the avatar's slot — the owner's colour is
                    already on the card's left border. */}
                {selecting ? (
                    <Checkbox
                        className="card-check"
                        size="small"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => selection.onToggle(opportunity)}
                        inputProps={{ 'aria-label': `Select ${personName}` }}
                    />
                ) : showOwner && (
                    <div className="rep-avatar" style={{ background: color }} title={owner.name || ''}>
                        {initialsOf(owner.name)}
                    </div>
                )}
            </div>
            <div className="card-bottom">
                <div className="card-vehicle" title={vehicle}>
                    {/* A picked card's titles stay plain text: a nested quote on
                        top of the one being attached is a second worksheet
                        nobody asked for, so the click falls through to the
                        card's own toggle. */}
                    {quotes.length === 0 ? vehicle : quotes.map((q, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && ', '}
                            {q.id && !selecting ? (
                                <a
                                    href={`/quotes/${q.id}`}
                                    className="card-quote-link"
                                    draggable={false}
                                    onClick={(e) => openQuote(e, q.id)}
                                >
                                    {q.description}
                                </a>
                            ) : q.description}
                        </React.Fragment>
                    ))}
                </div>
                <div className="card-date">{createdLabel(opportunity.created_at)}</div>
            </div>
            {/* Touch has no drag-and-drop, so on mobile a stage picker moves the
                card between columns. Hidden on desktop (drag is used there), and
                while picking — the modal must not move anyone's pursuit. */}
            {!selecting && stages?.length > 0 && onMove && (
                <div className="card-move-row">
                    <span className="card-move-label">Move to</span>
                    <select
                        className="card-move"
                        value={opportunity.stage || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            e.stopPropagation()
                            const stageId = Number(e.target.value)
                            if (stageId && stageId !== opportunity.stage) onMove(opportunity.id, stageId)
                        }}
                    >
                        {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
            )}
        </div>
    )
}
